/**
 * Redacts URL fragments (`#tok-123`) out of every Sentry field that can carry one, so the guest
 * access token never reaches Sentry even though `/p#<token>` is the entry URL (ADR-024 rules 4, 5).
 *
 * Two hooks apply this, composing with the existing `beforeSend` (`scrubSentryEvent`, EW-T18) rather
 * than replacing it:
 *  - `beforeBreadcrumb` catches navigation and fetch/xhr breadcrumbs as they are recorded.
 *  - `beforeSend` catches the final event's `request.url` and `transaction` name, plus a second pass
 *    over breadcrumbs as defence in depth for any that were attached before `beforeBreadcrumb` ran.
 */
import type { Breadcrumb, ErrorEvent, Event } from '@sentry/react';

/** Cuts a URL's fragment (everything from `#` onward) and replaces it with a fixed marker. */
export function redactUrlFragment(url: string): string {
  const i = url.indexOf('#');
  return i === -1 ? url : `${url.slice(0, i)}#[redacted]`;
}

/**
 * Query-param name fragments that mark a URL as carrying upload/request-signing credentials —
 * a signed `PUT` url (requestClientFileUpload/requestBrandAssetUpload/requestDocumentUpload/…)
 * puts the credential in the QUERY STRING, not the fragment, so `redactUrlFragment` alone misses
 * it. Matched case-insensitively against each query param's key. `signature`/`credential` cover
 * GCS (`X-Goog-Signature`, `X-Goog-Credential`) and S3 (`X-Amz-Signature`, `X-Amz-Credential`)
 * presigned URLs; `token`/`sig` cover other signing schemes without naming a specific provider.
 */
const CREDENTIAL_QUERY_MARKERS = ['signature', 'credential', 'token', 'sig'];

/** Cuts a URL's ENTIRE query string when any param name looks like a signing credential. */
export function redactCredentialedQuery(url: string): string {
  const qIndex = url.indexOf('?');
  if (qIndex === -1) return url;
  const hashIndex = url.indexOf('#', qIndex);
  const query = url.slice(qIndex + 1, hashIndex === -1 ? undefined : hashIndex);
  const hasCredential = query.split('&').some((pair) => {
    const key = (pair.split('=')[0] ?? '').toLowerCase();
    return CREDENTIAL_QUERY_MARKERS.some((marker) => key.includes(marker));
  });
  if (!hasCredential) return url;
  const suffix = hashIndex === -1 ? '' : url.slice(hashIndex);
  return `${url.slice(0, qIndex)}?[redacted]${suffix}`;
}

/** Composes both redactions — applied at every call site below instead of `redactUrlFragment` alone. */
function scrubUrlSensitiveParts(url: string): string {
  return redactCredentialedQuery(redactUrlFragment(url));
}

/** `beforeBreadcrumb`: redact the fragment on navigation (`from`/`to`) and fetch/xhr (`url`) breadcrumbs. */
export function scrubUrlFragmentsFromBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const data = breadcrumb.data;
  if (!data) return breadcrumb;

  if (breadcrumb.category === 'navigation') {
    const next: Record<string, unknown> = { ...data };
    if (typeof next.from === 'string') next.from = redactUrlFragment(next.from);
    if (typeof next.to === 'string') next.to = redactUrlFragment(next.to);
    return { ...breadcrumb, data: next };
  }

  if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
    if (typeof data.url === 'string') {
      return { ...breadcrumb, data: { ...data, url: scrubUrlSensitiveParts(data.url) } };
    }
  }

  return breadcrumb;
}

/** `beforeSend`: redact the fragment on `request.url`, `transaction`, and any surviving breadcrumb URLs. */
export function scrubUrlFragmentsFromEvent(event: ErrorEvent): ErrorEvent {
  const out: ErrorEvent = { ...event };

  if (typeof out.transaction === 'string') out.transaction = redactUrlFragment(out.transaction);
  if (out.request?.url) out.request = { ...out.request, url: scrubUrlSensitiveParts(out.request.url) };
  if (out.breadcrumbs) out.breadcrumbs = out.breadcrumbs.map(scrubUrlFragmentsFromBreadcrumb);

  return out;
}

/**
 * `beforeSendTransaction` (ADR-024 rule 5): `beforeSend` is never called for performance-transaction
 * events — Sentry routes those through a separate hook — so `transaction` name, `request.url`, the
 * trace context's `http.url`, and each span's own `http.url` attribute all need their own pass here.
 * `browserTracingIntegration` records the full `location.href`, fragment included, on these fields.
 */
export function scrubUrlFragmentsFromTransaction<T extends Event>(event: T): T {
  const out: T = { ...event };

  if (typeof out.transaction === 'string') out.transaction = redactUrlFragment(out.transaction);
  if (out.request?.url) out.request = { ...out.request, url: scrubUrlSensitiveParts(out.request.url) };

  const contexts = out.contexts;
  const trace = contexts?.trace;
  if (contexts && trace) {
    const nextData: Record<string, unknown> = { ...trace.data };
    // Sentry 10.45 writes the fragment-bearing URL under more than one key, depending on which
    // instrumentation produced the span (`@sentry/core` `fetch.js`, `utils/url.js`): `http.url` is
    // the one this file originally scrubbed; `url.fragment` and `url.full` are two more that carry
    // the same raw href. `http.query`/`url.query` carry the RAW QUERY STRING (fetch.js:311,
    // request.js:264) — the credential leak this file was extended to catch lives there, not in the
    // fragment, so these need `redactCredentialedQuery` even where no `#` is present. Each is
    // scrubbed independently — redacting one must not assume the others are absent.
    for (const key of ['http.url', 'url.fragment', 'url.full', 'http.query', 'url.query'] as const) {
      const value = nextData[key];
      if (typeof value === 'string') nextData[key] = scrubUrlSensitiveParts(value);
    }
    out.contexts = { ...contexts, trace: { ...trace, data: nextData } };
  }

  if (out.spans) {
    out.spans = out.spans.map((span) => {
      const data = span.data;
      if (!data) return span;
      const nextData: typeof data = { ...data };
      // `url` (raw), `http.fragment` (the hash alone), and `http.query` (the raw query string,
      // fetch.js:311/request.js:264 — where a signed-URL credential actually lives) are the other
      // keys `fetch.js`/`request.js` write alongside `http.url` — same rationale as the
      // trace-context loop above.
      for (const key of ['http.url', 'url', 'http.fragment', 'http.query'] as const) {
        const value = nextData[key];
        if (typeof value === 'string') nextData[key] = scrubUrlSensitiveParts(value);
      }
      return { ...span, data: nextData };
    });
  }

  if (out.breadcrumbs) out.breadcrumbs = out.breadcrumbs.map(scrubUrlFragmentsFromBreadcrumb);

  return out;
}
